// src/utils/fullscreenService.js
class FullscreenService {
  constructor() {
    this.isFullscreen = false;
    this.element = null;
    this.bindEvents();
  }

  // Request fullscreen
  async enterFullscreen(element = document.documentElement) {
    this.element = element;
    
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen(); // Safari
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen(); // IE/Edge
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen(); // Firefox
      }
      this.isFullscreen = true;
      return true;
    } catch (error) {
      console.error('Fullscreen error:', error);
      this.isFullscreen = false;
      return false;
    }
  }

  // Exit fullscreen
  async exitFullscreen() {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      }
      this.isFullscreen = false;
      return true;
    } catch (error) {
      console.error('Exit fullscreen error:', error);
      return false;
    }
  }

  // Toggle fullscreen
  async toggleFullscreen(element = document.documentElement) {
    if (this.isFullscreen) {
      return await this.exitFullscreen();
    } else {
      return await this.enterFullscreen(element);
    }
  }

  // Bind event listeners
  bindEvents() {
    document.addEventListener('fullscreenchange', this.handleFullscreenChange.bind(this));
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange.bind(this));
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange.bind(this));
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange.bind(this));
  }

  handleFullscreenChange() {
    this.isFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );

    // Dispatch custom event
    const event = new CustomEvent('fullscreenChanged', { 
      detail: { isFullscreen: this.isFullscreen } 
    });
    document.dispatchEvent(event);

        // Update body class for CSS styling
    if (this.isFullscreen) {
      document.body.classList.add('fullscreen-mode');
      // Ensure fullscreen mode allows scrolling and zooming on mobile
      document.body.style.overflowY = 'auto';
      document.body.style.overflowX = 'hidden';
      document.body.style.touchAction = 'pan-x pan-y pinch-zoom';
      document.body.style.webkitOverflowScrolling = 'touch';
    } else {
      document.body.classList.remove('fullscreen-mode');
      // Reset body styles when exiting fullscreen
      document.body.style.overflowY = '';
      document.body.style.overflowX = '';
      document.body.style.touchAction = '';
      document.body.style.webkitOverflowScrolling = '';
    }
  }

  // Check if fullscreen is supported
  isSupported() {
    return !!(
      document.requestFullscreen ||
      document.webkitRequestFullscreen ||
      document.msRequestFullscreen ||
      document.mozRequestFullScreen
    );
  }

  // Get current fullscreen state
  getFullscreenState() {
    return this.isFullscreen;
  }
}

// Create singleton instance
export const fullscreenService = new FullscreenService();